import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { useReseedHolidays } from '../../hooks/useReseedHolidays';

interface ReseedHolidaysButtonProps {
  year: number;
}

/** Deletes and regenerates official holidays for `year` only — company-added holidays are untouched. */
export function ReseedHolidaysButton({ year }: ReseedHolidaysButtonProps) {
  const { t } = useTranslation('timeOff');
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();
  const reseedHolidays = useReseedHolidays();

  async function handleClick() {
    const result = await confirm({
      title: t('settings.holidays.reseedConfirmTitle', { year }),
      description: t('settings.holidays.reseedConfirmDescription'),
    });
    if (!result.confirmed) return;
    reseedHolidays.mutate({ year });
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleClick}
        disabled={reseedHolidays.isPending}
        data-testid="time-off-settings-reseed-btn"
      >
        <RefreshCw className="size-4" />
        {t('settings.holidays.reseed')}
      </Button>

      {isOpen && options && (
        <ConfirmDialog
          open={isOpen}
          onOpenChange={(open) => !open && handleCancel()}
          title={options.title}
          description={options.description}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          variant={options.variant}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
}
